import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const PARTNERS = [
  {
    name: "Architekturbüro Klaus Bornschlegel",
    trade: "Architektur · Planung · Bauleitung",
    location: "Kulmbach",
    url: "https://www.architekt-klaus-bornschlegel.de/index.html",
  },
  {
    name: "Grimm Dachdecker",
    trade: "Dachdeckerei · Spenglerei",
    location: "Selb",
    url: "https://www.grimm-dachdecker.de/Home",
  },
  {
    name: "Heizöl Schramm",
    trade: "Heizöl · Pellets · Energieversorgung",
    location: "Helmbrechts",
    url: "https://www.heizoel-schramm.de",
  },
  {
    name: "Münzer Haustechnik",
    trade: "Haustechnik · Sanitär",
    location: "Hochfranken",
    url: "https://www.muenzer-haustechnik.de",
  },
  {
    name: "Notarin Wengenmayer",
    trade: "Notariat · Immobilien & Beurkundung",
    location: "Hof",
    url: "https://www.notarin-wengenmayer.de",
  },
  {
    name: "Raithel Elektrotechnik",
    trade: "Elektrotechnik · Elektroinstallation",
    location: "Hof",
    url: "https://www.raithel-etec.de",
  },
  {
    name: "Schreinerei Stöhr",
    trade: "Schreiner · Türen & Fenster",
    location: "Hof",
    url: "https://www.schreinerei-stoehr.de",
  },
  {
    name: "Sparkasse Hochfranken",
    trade: "Finanzierung · Bank",
    location: "Wunsiedel",
    url: "https://www.sparkasse-hochfranken.de/de/home.html",
  },
  {
    name: "Stübiger Haustechnik",
    trade: "Haustechnik · Heizung · Sanitär",
    location: "Münchberg",
    url: "https://stuebiger-haustechnik.info",
  },
  {
    name: "Versicherungskammer Bayern",
    trade: "Versicherung",
    location: "München",
    url: "https://www.vkb.de",
  },
];

export default function Partner() {
  return (
    <div data-testid="page-partner">
      <section className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-12">
        <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary mb-6">
          <span className="inline-block w-2 h-2 bg-accent" />
          Partner
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.05] max-w-4xl">
          Gemeinsam mit Handwerk und <em className="italic">guten Partnern</em> aus der Region.
        </h1>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 lg:col-start-2 space-y-6 text-foreground/90 leading-relaxed">
            <p className="text-lg">
              Gute Sanierungen entstehen nicht am Schreibtisch. Sie entstehen
              dort, wo Handwerk verstanden und gelebt wird — auf der Baustelle,
              im Gespräch mit Fachleuten, die wissen, wie ein altes Haus
              funktioniert.
            </p>
            <p>
              Wir arbeiten seit Jahren mit einem festen Kreis oberfränkischer
              Fachbetriebe zusammen. Viele davon sind seit Generationen am
              Standort eingesessen und kennen die Bausubstanz der Region wie
              kaum jemand sonst. Diese verlässliche Zusammenarbeit ist eine
              wichtige Grundlage dafür, dass wir unsere Häuser über Jahre
              behutsam und konsequent sanieren können.
            </p>
            <p>
              Eine Auswahl der Partner, mit denen wir regelmäßig
              zusammenarbeiten und denen wir vertrauen:
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-32">
        <div className="border-t border-border max-w-5xl mx-auto" data-testid="partner-list">
          {PARTNERS.map((p) => (
            <a
              key={p.url}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`partner-${p.url}`}
              className="group flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-6 sm:py-8 border-b border-border hover:bg-muted/40 transition-colors duration-200 px-4 -mx-4"
            >
              <div>
                <div className="font-serif text-2xl sm:text-3xl text-foreground group-hover:text-primary transition-colors">
                  {p.name}
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2 flex-wrap">
                  <span>{p.trade}</span>
                  <span className="inline-block w-1 h-1 bg-accent rounded-none" />
                  <span>{p.location}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-primary opacity-70 group-hover:opacity-100 transition-opacity">
                <span className="hidden sm:inline tracking-wide">Website besuchen</span>
                <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} />
              </div>
            </a>
          ))}
        </div>
        <p className="mt-10 text-sm text-muted-foreground max-w-2xl">
          Sie sind selbst ein Handwerksbetrieb in Hochfranken und möchten mit
          uns zusammenarbeiten? Wir freuen uns über Ihre Kontaktaufnahme.
        </p>
        <Link to="/kontakt" data-testid="partner-cta-contact">
          <Button className="mt-6 rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-10 text-sm tracking-wide">
            Als Partner vorstellen <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
        <p className="mt-12 pt-8 border-t border-border text-xs text-muted-foreground max-w-2xl leading-relaxed" data-testid="partner-disclaimer">
          Hinweis: Die auf dieser Seite genannten Unternehmen sind eigenständige
          Drittanbieter. Keppel Immobilien übernimmt keine Haftung oder
          Verantwortung für deren Inhalte, Websites, Leistungen oder Angebote.
          Für verlinkte externe Seiten sind ausschließlich deren Betreiber
          verantwortlich.
        </p>
      </section>
    </div>
  );
}
